<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header (title only; tabs + type chips below) -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            v-if="id"
            type="button"
            class="amf-header__btn amf-header__btn--create"
            :aria-label="$t(type)"
            @click="goTo(type)"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            v-if="!formType"
            type="button"
            class="amf-header__btn"
            :aria-label="$t(type + 'List')"
            @click="goTo(type + 'List')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ $t(type) }}</h1>
        <button
          v-if="!formType"
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>
      <!-- Keep type model wired (hidden); type is route-driven -->
      <v-radio-group v-model="type" class="mt-0 d-none" dense hide-details row>
        <v-radio
          :label="$t('payCheck')"
          value="payCheck"
          :disabled="id || formType ? true : false"
        />
        <v-radio
          :label="$t('recieveCheck')"
          value="recieveCheck"
          :disabled="id || formType ? true : false"
        />
      </v-radio-group>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t(type) }}
      </v-toolbar-title>
      <v-checkbox
        v-show="$store.state.isMainYear"
        v-model="initialPeriod"
        class="mt-0 mx-2 app-form-toolbar__type"
        dense
        hide-details
        :label="$t('initialPeriod')"
        value="active"
      />
      <v-radio-group v-model="type" class="mt-0 d-none" dense hide-details row>
        <v-radio
          :label="$t('payCheck')"
          value="payCheck"
          :disabled="id || formType ? true : false"
        />
        <v-radio
          :label="$t('recieveCheck')"
          value="recieveCheck"
          :disabled="id || formType ? true : false"
        />
      </v-radio-group>
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip v-if="id" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type)"
            >
              <v-icon color="teal"> mdi-plus-box-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type) }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!formType"
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type + 'List')"
            >
              <v-icon color="primary"> mdi-format-list-numbered </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type + "List") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!formType"
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary"> mdi-arrow-left </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("back") }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <!-- Mobile: tabs only (type is route-driven; no pay/receive switch) -->
      <div v-if="isMobile && !isEmbedded" class="amf-mobile-nav" dir="rtl">
        <div class="amf-mobile-nav__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 0 }"
            :aria-selected="formTab === 0"
            @click="formTab = 0"
          >
            {{ $t("public-information") }}
          </button>
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 1 }"
            :aria-selected="formTab === 1"
            :disabled="!documentPermmission"
            @click="documentPermmission && (formTab = 1)"
          >
            {{ $t("documents") }}
          </button>
        </div>
        <div class="amf-header__type">
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': type === 'payCheck' }"
            :disabled="!!id || !!formType"
            @click="!id && !formType && goTo('payCheck')"
          >
            {{ $t("payCheck") }}
          </button>
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': type === 'recieveCheck' }"
            :disabled="!!id || !!formType"
            @click="!id && !formType && goTo('recieveCheck')"
          >
            {{ $t("recieveCheck") }}
          </button>
        </div>
        <div v-show="$store.state.isMainYear" class="amf-mobile-nav__extra">
          <v-checkbox
            v-model="initialPeriod"
            class="mt-0 amf-header__check"
            dense
            hide-details
            :label="$t('initialPeriod')"
            value="active"
          />
        </div>
      </div>

      <v-tabs
        v-model="formTab"
        class="app-form-page__tabs"
        :class="{ 'app-form-page__tabs--mobile-bar-hidden': isMobile && !isEmbedded }"
        :show-arrows="isMobile"
      >
        <v-tab>
          {{ $t("public-information") }}
        </v-tab>
        <v-tab :disabled="!documentPermmission">
          {{ $t("documents") }}
        </v-tab>
        <v-tab-item>
          <!-- Mobile summary (same v-models as desktop) -->
          <div v-if="isMobile && !isEmbedded" class="amf-summary" dir="rtl">
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refParty')"
            >
              <span class="amf-summary__label">{{ type == 'payCheck' ? $t('reciever') : $t('payer') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !account }"
              >{{ mobileAccountText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              :disabled="amountDisable"
              @click="!amountDisable && openAmountNumpad()"
            >
              <span class="amf-summary__label">{{ $t('amount') }}</span>
              <span class="amf-summary__value">{{ mobileAmountText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              :disabled="dueDateDisable"
              @click="!dueDateDisable && (dueDateSheet = true)"
            >
              <span class="amf-summary__label">{{ $t('dueDate') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !dueDate }"
              >{{ dueDate || '—' }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="dateSheet = true">
              <span class="amf-summary__label">{{ $t('date') + ' ' + $t('issue') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !date }"
              >{{ date || '—' }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              :disabled="kindDisable"
              @click="!kindDisable && (kindSheet = true)"
            >
              <span class="amf-summary__label">{{ $t('kind') + ' ' + $t('check') }}</span>
              <span class="amf-summary__value">{{ mobileKindText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="type == 'recieveCheck'"
              type="button"
              class="amf-summary__row"
              @click="checkIdentitySheet = true"
            >
              <span class="amf-summary__label">{{ $t('number') + ' ' + $t('check') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !number }"
              >{{ number || '—' }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="type == 'payCheck'"
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refPayCheck')"
            >
              <span class="amf-summary__label">{{ $t('check') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !check }"
              >{{ mobileCheckText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="detailsSheet = true">
              <span class="amf-summary__label">{{ $t('check') }} — {{ $t('otherInputs') }}</span>
              <span class="amf-summary__value amf-summary__value--muted">{{ mobileDetailsHint }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              :disabled="type == 'payCheck'"
              @click="type != 'payCheck' && openMobileAutocomplete('refCurrency')"
            >
              <span class="amf-summary__label">{{ $t('currency') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !currency }"
              >{{ mobileCurrencyText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              :disabled="currencyAmountDisable"
              @click="!currencyAmountDisable && openRateNumpad()"
            >
              <span class="amf-summary__label">{{ $t('currency-rate') }}</span>
              <span class="amf-summary__value">{{ mobileRateText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row amf-summary__row--tall"
              @click="otherSheet = true"
            >
              <span class="amf-summary__label">{{ $t('otherInputs') }}</span>
              <span class="amf-summary__value amf-summary__value--muted">{{ $t('other-inputs-hint') }}</span>
              <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="documentPermmission"
              type="button"
              class="amf-summary__row"
              @click="formTab = 1"
            >
              <span class="amf-summary__label">{{ $t('documents') }}</span>
              <span class="amf-summary__value">{{
                hasAttachmentFiles
                  ? attachments.length + ' فایل'
                  : $t('no-data-found')
              }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <div class="amf-sr-only" aria-hidden="true">
              <v-text-field v-model="date" :rules="[(v) => !!v || $t('is-required')]" />
              <v-text-field v-model="dueDate" :rules="[(v) => !!v || $t('is-required')]" />
              <v-text-field v-model="amount" :rules="rule" />
              <v-text-field
                v-if="type == 'recieveCheck'"
                v-model="number"
                :rules="[(v) => !!v || $t('is-required')]"
              />
              <autocomplete
                ref="refParty"
                v-model="account"
                :title="type == 'payCheck' ? $t('reciever') : $t('payer')"
                type="person"
                :new-btn="true"
                :value="account"
              />
              <autocomplete
                v-if="type == 'payCheck'"
                ref="refPayCheck"
                v-model="check"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('check')"
                type="payCheck"
                :value="check"
              />
              <autocomplete
                ref="refCurrency"
                v-model="currency"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('currency')"
                type="currency"
                :init="defaultCurrency"
                :value="currency"
                :disabled="type == 'payCheck' ? true : false"
                @change="changeCurrency"
              />
            </div>
          </div>

          <!-- Desktop / embedded: unchanged card layout -->
          <div v-if="!isMobile || isEmbedded" class="app-form-body">
            <v-container fluid class="app-form-container app-form-container--cards pa-3">
              <v-row class="app-form-card-grid ma-0" dense>
                <!-- Main check card (right) + notifications fill leftover height -->
                <v-col cols="12" md="7" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">
                          {{
                            type === "payCheck"
                              ? "mdi-bank-transfer-out"
                              : "mdi-bank-transfer-in"
                          }}
                        </v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("public-information") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{
                            type === "payCheck"
                              ? $t("pay-check-main-info-hint")
                              : $t("receive-check-main-info-hint")
                          }}
                        </div>
                      </div>
                    </div>
                    <div
                      class="app-form-card__body app-form-card__body--stack app-form-card__body--with-notify"
                    >
                      <v-row class="app-form-fields ma-0">
                        <!-- row: account | amount | dueDate -->
                        <v-col cols="12" sm="4">
                          <autocomplete
                            v-model="account"
                            :title="type == 'payCheck' ? $t('reciever') : $t('payer')"
                            type="person"
                            :new-btn="true"
                            :value="account"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <money
                            v-model="amount"
                            :label="$t('amount')"
                            :dense="true"
                            :outlined="true"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :disabled="amountDisable"
                            :options="options"
                            :rules="rule"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <template>
                            <v-text-field
                              id="dueDateId"
                              v-model="dueDate"
                              :label="$t('dueDate')"
                              clear-icon="flaticon2-close-cross"
                              outlined
                              dense
                              clearable
                              hide-details="auto"
                              :disabled="dueDateDisable"
                              :rules="[(v) => !!v || $t('is-required')]"
                              @keyup="dueDate = null"
                            />
                            <date-picker
                              v-model="dueDate"
                              element="dueDateId"
                              :locale="$store.state.date_locale"
                              :format="$store.state.date_format"
                              auto-submit
                              :color="
                                this.$vuetify.theme.themes[
                                  this.$vuetify.theme.isDark ? 'dark' : 'light'
                                ].primary
                              "
                              @close="setDate"
                            />
                          </template>
                        </v-col>
                        <!-- row: issue date | kind | check number / pay check -->
                        <v-col cols="12" sm="4">
                          <template>
                            <v-text-field
                              id="from"
                              v-model="date"
                              :label="$t('date') + ' ' + $t('issue')"
                              :rules="[(v) => !!v || $t('is-required')]"
                              clear-icon="flaticon2-close-cross"
                              outlined
                              dense
                              clearable
                              hide-details="auto"
                            />
                            <date-picker
                              v-model="date"
                              element="from"
                              :locale="$store.state.date_locale"
                              :format="$store.state.date_format"
                              auto-submit
                              :min="$store.state.startDate"
                              :max="$store.state.endDate"
                              :color="
                                this.$vuetify.theme.themes[
                                  this.$vuetify.theme.isDark ? 'dark' : 'light'
                                ].primary
                              "
                              @close="setDate"
                            />
                          </template>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-select
                            v-model="kind"
                            :label="$t('kind') + ' ' + $t('check')"
                            :items="kindItems"
                            height="30"
                            dense
                            outlined
                            hide-details="auto"
                            :disabled="kindDisable"
                            item-value="value"
                            item-text="text"
                          />
                        </v-col>
                        <v-col
                          v-if="type == 'recieveCheck'"
                          cols="12"
                          sm="4"
                        >
                          <v-text-field
                            v-model="number"
                            :label="$t('number') + ' ' + $t('check')"
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                            :rules="[(v) => !!v || $t('is-required')]"
                          />
                        </v-col>
                        <v-col v-if="type == 'payCheck'" cols="12" sm="4">
                          <autocomplete
                            v-model="check"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('check')"
                            type="payCheck"
                            :value="check"
                          />
                        </v-col>
                        <!-- row: sayad | bank | account number (receive) / sayad alone (pay) -->
                        <v-col cols="12" sm="4">
                          <v-text-field
                            v-model="fishermanNumber"
                            :label="$t('fishermanNumber')"
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col
                          v-if="type == 'recieveCheck'"
                          cols="12"
                          sm="4"
                        >
                          <v-text-field
                            v-model="bankName"
                            :label="$t('name') + ' ' + $t('bank')"
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col
                          v-if="type == 'recieveCheck'"
                          cols="12"
                          sm="4"
                        >
                          <v-text-field
                            v-model="bankAccountNumber"
                            :label="$t('number') + ' ' + $t('account')"
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col
                          v-if="type == 'recieveCheck'"
                          cols="12"
                          sm="4"
                        >
                          <autocomplete
                            v-model="cashBox"
                            :title="$t('cashBox')"
                            type="getCash"
                            :currency-prop="
                              currency ? currency.id : $store.state.currency.id
                            "
                            :value="cashBox"
                          />
                        </v-col>
                        <v-col
                          v-if="type == 'recieveCheck'"
                          cols="12"
                          sm="4"
                        >
                          <v-text-field
                            v-model="owner_name"
                            :label="$t('check_owner_name')"
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                          />
                        </v-col>
                      </v-row>
                      <div class="app-form-card__notify">
                        <notification v-model="notifications" :value="notifications" />
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Other inputs card (left) -->
                <v-col cols="12" md="5" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("otherInputs") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{ $t("other-inputs-hint") }}
                        </div>
                      </div>
                    </div>
                    <div class="app-form-card__body app-form-card__body--stack">
                      <v-row class="app-form-fields ma-0">
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="branch"
                            :title="$t('branch')"
                            type="branch"
                            :set-init="true"
                            :value="branch"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <autocomplete
                            ref="refCurrency"
                            v-model="currency"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('currency')"
                            type="currency"
                            :init="defaultCurrency"
                            :value="currency"
                            :disabled="type == 'payCheck' ? true : false"
                            @change="changeCurrency"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <money
                            v-model="currency_amount"
                            :label="$t('currency-rate')"
                            :dense="true"
                            :outlined="true"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :disabled="currencyAmountDisable"
                            :options="options"
                            :rules="rule"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <tag v-model="tag" :solo="false" :input="tag" :height="25" />
                        </v-col>
                        <v-col v-if="!initialPeriod" cols="12">
                          <autocomplete
                            ref="refRelations"
                            v-model="relations"
                            :multiple="true"
                            :value="[]"
                            :disabled="!account || relationsDisable ? true : false"
                            :data="
                              JSON.stringify({
                                refId: account ? account.id : null,
                                currency_id: currency ? currency.id : null,
                              })
                            "
                            :title="$t('relative-document')"
                            :type="
                              type == 'payCheck' ? 'receiptInvoice' : 'remittanceInvoice'
                            "
                          />
                        </v-col>
                        <v-col cols="12" class="app-form-fields__grow">
                          <description
                            ref="refContent"
                            v-model="content"
                            class="payment-description"
                            :outlined="true"
                            :rows="4"
                            type="sell"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="app-form-body">
            <v-container class="app-form-container app-form-documents pt-3 pb-2">
              <form-attachment-doc
                ref="refFilePicker"
                v-model="attachments"
                type-prop="check"
                :val="id && attachments ? attachments : null"
              />
            </v-container>
          </div>
        </v-tab-item>
      </v-tabs>

      <!-- Mobile layers (same v-models / create-update as desktop) -->
      <template v-if="isMobile && !isEmbedded">
        <v-bottom-sheet v-model="dueDateSheet">
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('dueDate') }}</div>
              <button type="button" class="amf-sheet__back" @click="dueDateSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-mobile-date" dir="rtl">
              <div class="amf-mobile-date__selected">
                <span class="amf-mobile-date__label">{{ $t('dueDate') }}</span>
                <strong class="amf-mobile-date__value">{{ dueDate || '—' }}</strong>
                <button
                  v-if="dueDate"
                  type="button"
                  class="amf-mobile-date__clear"
                  @click="dueDate = null"
                >
                  {{ $t("clear") }}
                </button>
              </div>
              <date-picker
                v-model="dueDate"
                inline
                simple
                auto-submit
                type="date"
                :locale="$store.state.date_locale"
                :format="$store.state.date_format"
                class="amf-mobile-date__picker"
                color="#7B8BA3"
                @close="setDate"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="dueDateSheet = false">
                {{ $t("Save") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="dateSheet">
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('date') + ' ' + $t('issue') }}</div>
              <button type="button" class="amf-sheet__back" @click="dateSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-mobile-date" dir="rtl">
              <div class="amf-mobile-date__selected">
                <span class="amf-mobile-date__label">{{ $t('date') + ' ' + $t('issue') }}</span>
                <strong class="amf-mobile-date__value">{{ date || '—' }}</strong>
                <button
                  v-if="date"
                  type="button"
                  class="amf-mobile-date__clear"
                  @click="date = null"
                >
                  {{ $t("clear") }}
                </button>
              </div>
              <date-picker
                v-model="date"
                inline
                simple
                auto-submit
                type="date"
                :locale="$store.state.date_locale"
                :format="$store.state.date_format"
                :min="$store.state.startDate"
                :max="$store.state.endDate"
                class="amf-mobile-date__picker"
                color="#7B8BA3"
                @close="setDate"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="dateSheet = false">
                {{ $t("Save") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="kindSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('kind') + ' ' + $t('check') }}</div>
              <button type="button" class="amf-sheet__back" @click="kindSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-select
                v-model="kind"
                :label="$t('kind') + ' ' + $t('check')"
                :items="kindItems"
                dense
                outlined
                hide-details="auto"
                :disabled="kindDisable"
                item-value="value"
                item-text="text"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="kindSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-if="type == 'recieveCheck'" v-model="checkIdentitySheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">
                {{ $t('number') + ' ' + $t('check') }}
              </div>
              <button type="button" class="amf-sheet__back" @click="checkIdentitySheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-text-field
                v-model="number"
                :label="$t('number') + ' ' + $t('check')"
                dense
                outlined
                clearable
                hide-details="auto"
                :rules="[(v) => !!v || $t('is-required')]"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="checkIdentitySheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="detailsSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('check') }}</div>
              <button type="button" class="amf-sheet__back" @click="detailsSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <v-text-field
                v-model="fishermanNumber"
                :label="$t('fishermanNumber')"
                dense
                outlined
                clearable
                hide-details="auto"
              />
              <template v-if="type == 'recieveCheck'">
                <v-text-field
                  v-model="bankName"
                  :label="$t('name') + ' ' + $t('bank')"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                />
                <v-text-field
                  v-model="bankAccountNumber"
                  :label="$t('number') + ' ' + $t('account')"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                />
                <autocomplete
                  v-model="cashBox"
                  :title="$t('cashBox')"
                  type="getCash"
                  :currency-prop="currency ? currency.id : $store.state.currency.id"
                  :value="cashBox"
                />
                <v-text-field
                  v-model="owner_name"
                  :label="$t('check_owner_name')"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                />
              </template>
              <notification v-model="notifications" :value="notifications" />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="detailsSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="otherSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('otherInputs') }}</div>
              <button type="button" class="amf-sheet__back" @click="otherSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <autocomplete
                v-model="branch"
                :title="$t('branch')"
                type="branch"
                :set-init="true"
                :value="branch"
              />
              <tag v-model="tag" :solo="false" :input="tag" :height="25" />
              <autocomplete
                v-if="!initialPeriod"
                ref="refRelations"
                v-model="relations"
                :multiple="true"
                :value="[]"
                :disabled="!account || relationsDisable ? true : false"
                :data="
                  JSON.stringify({
                    refId: account ? account.id : null,
                    currency_id: currency ? currency.id : null,
                  })
                "
                :title="$t('relative-document')"
                :type="type == 'payCheck' ? 'receiptInvoice' : 'remittanceInvoice'"
              />
              <description
                ref="refContent"
                v-model="content"
                class="payment-description"
                :outlined="true"
                :rows="4"
                type="sell"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="otherSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
          <v-card class="amf-numpad" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-numpad__header">
              <span class="amf-numpad__header-spacer" />
              <div class="amf-numpad__title">{{ numpadLabel }}</div>
              <button type="button" class="amf-sheet__back" @click="numpadOpen = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-numpad__display">
              <div class="amf-numpad__display-label">{{ numpadLabel }}</div>
              <div class="amf-numpad__display-value">{{ numpadDisplay }}</div>
            </div>
            <div class="amf-numpad__pad" dir="ltr">
              <div class="amf-numpad__digits">
                <button
                  v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
                  :key="'d-' + n"
                  type="button"
                  class="amf-numpad__key"
                  @click="numpadDigit(n)"
                >
                  {{ n }}
                </button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('00')">00</button>
                <button type="button" class="amf-numpad__key" @click="numpadDigit(0)">0</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('000')">000</button>
              </div>
              <div class="amf-numpad__actions">
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(1)">+</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(-1)">-</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadClear">C</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadDot">.</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadBackspace">
                  <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
                </button>
              </div>
            </div>
            <div class="amf-numpad__footer">
              <button type="button" class="amf-sheet__btn" @click="confirmNumpad">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>
      </template>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            v-shortkey="['shift', 'f3']"
            @shortkey="create"
            @click="create"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            v-shortkey="['shift', 'f3']"
            @shortkey="update"
            @click="update"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="!formType"
            color="primary"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo(type + 'List')"
          >
            {{ $t(type + "List") }}
          </v-btn>
        </div>
      </div>
    </v-form>
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
// import aicomplete from '../../components/general/AIcomplete/index'
import General from "../../../../Mixins/general";
import Description from "../../components/general/description/Description";
import FormAttachmentDoc from "../../components/general/FormAttachmentDoc";
export default {
  name: "Product",
  components: {
    autocomplete: () => import("../../components/general/patial/autocomplete"),
    // aicomplete,
    FormAttachmentDoc,
    Description,
    Tag: () => import("../../components/general/Tag/tag"),
  },
  mixins: [General],
  props: ["formType", "accountProp", "typeProp", "notStore"],
  data: () => ({
    options: {
      locale: "en-US",
      prefix: "",
      suffix: " ",
      precision: 0,
    },
    render: false,
    isLoading: false,
    alert: false,
    cashBox: null,
    initialPeriod: null,
    fishermanNumber: null,
    notifications: null,
    attachments: null,
    account: null,
    date: null,
    check: null,
    dueDate: null,
    tag: null,
    content: null,
    valid: null,
    message: null,
    alertType: null,
    bankAccountNumber: null,
    amount: 0,
    relations: null,
    name: null,
    id: null,
    currency: null,
    currency_amount: null,
    status: "active",
    branch: null,
    bankName: null,
    kind: "normal",
    kindItems: [],
    number: null,
    type: "bank",
    amountDisable: false,
    kindDisable: false,
    relationsDisable: false,
    currencyAmountDisable: false,
    dueDateDisable: false,
    initial_price: null,
    loading: false,
    owner_name: null,
    snakbar: null,
    snakbarText: null,
    snakbarColor: null,
    formTab: 0,
    dueDateSheet: false,
    dateSheet: false,
    kindSheet: false,
    checkIdentitySheet: false,
    detailsSheet: false,
    otherSheet: false,
    numpadOpen: false,
    numpadField: null,
    numpadLabel: "",
    numpadDraft: "",
    numpadIsAmount: true,
  }),
  computed: {
    isEmbedded() {
      return !!(this.notStore || this.formType);
    },
    rule() {
      return [
        (v) =>
          (v && v.replaceAll(",", "") > 0) || this.$t("TheAmountMustBeBiggerThanZero"),
      ];
    },
    mobileAccountText() {
      if (!this.account) return "—";
      return this.account.name || this.account.title || "—";
    },
    mobileAmountText() {
      if (this.amount === null || this.amount === undefined || this.amount === "") {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.amount, "plain", 0)
        : String(this.amount);
    },
    mobileRateText() {
      if (
        this.currency_amount === null ||
        this.currency_amount === undefined ||
        this.currency_amount === ""
      ) {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.currency_amount, "plain", 0)
        : String(this.currency_amount);
    },
    mobileCurrencyText() {
      if (!this.currency) return "—";
      return this.currency.name || "—";
    },
    mobileKindText() {
      const item = (this.kindItems || []).find((i) => i.value === this.kind);
      return item ? item.text : this.kind || "—";
    },
    mobileCheckText() {
      if (!this.check) return "—";
      return (
        this.check.name ||
        this.check.title ||
        this.check.number ||
        this.check.label ||
        "—"
      );
    },
    mobileDetailsHint() {
      if (this.type === "recieveCheck") {
        return this.fishermanNumber || this.bankName || this.owner_name || "—";
      }
      return this.fishermanNumber || "—";
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft || "");
      if (!raw) return "0";
      if (!this.numpadIsAmount || !this.$numberFormat) return raw;
      if (raw.endsWith(".")) {
        const head = raw.slice(0, -1);
        return (head ? this.$numberFormat(head, "plain", 0) : "0") + ".";
      }
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return raw;
      return this.$numberFormat(n, "plain", 0);
    },
    hasAttachmentFiles() {
      return Array.isArray(this.attachments) && this.attachments.length > 0;
    },
  },
  watch: {
    defaultCurrency() {
      if (this.defaultCurrency) {
        this.$refs.refCurrency.setModel(this.defaultCurrency);
        this.currency_amount = this.defaultCurrency.amount;
      }
    },
    check() {
      if (this.check) {
        this.currency = this.check.currency;
      } else {
        this.currency = null;
      }
      if (this.$refs.refRelations) {
        this.$refs.refRelations.clear();
        this.$refs.refRelations.initList = false;
        this.$refs.refRelations.getItems({ query: null, page: 1 });
      }
      if (this.check.type === "customer") {
        this.dueDate = this.check.due_date;
        this.amount = this.check.amount;
        this.kind = this.check.kind;
        this.content = this.check.content;
        this.amountDisable = true;
        this.kindDisable = true;
        this.dueDateDisable = true;
        this.currencyAmountDisable = true;
        this.tag = this.check.tags;
        if (this.check.relations.length > 0) {
          this.relations = this.check.relations;
          this.relationsDisable = true;
        }
      } else {
        this.amountDisable = false;
        this.kindDisable = false;
        this.dueDateDisable = false;
        this.currencyAmountDisable = false;
        this.relationsDisable = false;
      }
    },
    currency(val, old) {
      if (val) {
        this.currency_amount = val.amount;
        this.options.suffix = val.symbol;
      } else {
        this.options.suffix = " ";
        this.currency_amount = null;
      }
      if (this.$refs.refRelations) {
        this.$refs.refRelations.clear();
        this.$refs.refRelations.initList = false;
        this.$refs.refRelations.getItems({ query: null, page: 1 });
      }
    },
    account() {
      if (this.$refs.refRelations) {
        this.$refs.refRelations.clear();
        this.$refs.refRelations.initList = false;
        this.$refs.refRelations.getItems({ query: null, page: 1 });
      }
    },
    isMobile(val) {
      if (this.isEmbedded) return;
      this.lockPageScroll(!!val);
    },
  },
  beforeMount() {
    if (this.$route.name === "payCheck") {
      this.type = "payCheck";
    } else {
      this.type = "recieveCheck";
    }
    if (this.formType) {
      this.type = this.formType;
    }
    this.setInitProp();

    if (this.$route.params.id != null && !this.accountProp) {
      this.show();
      this.id = this.$route.params.id;
      this.render = false;
    } else {
      this.render = true;
      this.setInitCurrency();
      // this.defaultCurrency = this.getDefault('defaultCurrency')
    }
  },
  mounted() {
    this.kindItems = [
      {
        value: "normal",
        text: this.$t("normal"),
      },
      {
        value: "guarantee",
        text: this.$t("guarantee"),
      },
    ];
    if (this.isMobile && !this.isEmbedded) {
      this.lockPageScroll(true);
    }
  },
  beforeDestroy() {
    this.lockPageScroll(false);
  },
  methods: {
    openMobileAutocomplete(refName) {
      const ref = this.$refs[refName];
      if (!ref || typeof ref.openMobileDialog !== "function") return;
      if (ref.disabled) return;
      ref.openMobileDialog();
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    openAmountNumpad() {
      this.numpadField = "amount";
      this.numpadLabel = this.$t("amount");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.amount !== null && this.amount !== undefined && this.amount !== ""
          ? String(this.amount)
          : "";
      this.numpadOpen = true;
    },
    openRateNumpad() {
      this.numpadField = "currency_amount";
      this.numpadLabel = this.$t("currency-rate");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.currency_amount !== null &&
        this.currency_amount !== undefined &&
        this.currency_amount !== ""
          ? String(this.currency_amount)
          : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        const parsed = raw === "" ? 0 : parseFloat(raw);
        this[this.numpadField] = Number.isNaN(parsed) ? 0 : parsed;
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    setInitProp() {
      if (this.accountProp) {
        this.account = this.accountProp;
      }
      if (this.typeProp) {
        this.type = this.typeProp;
      }
    },
    changeCurrency() {
      this.bankRemain = 0;
      this.currency_amount = this.currency.amount;
      this.options = {
        locale: "en-US",
        prefix: "",
        suffix: this.currency.symbol,
        precision: 0,
      };
    },
    create() {
      if (!this.$refs.form.validate()) return false;
      var app = this;
      app.alert = false;
      if (this.notStore) {
        const copy = Object.assign({}, this.$data);
        app.$emit("get", copy);
        app.$emit("close");
        this.clearForm();
        this.setInitProp();
      } else {
        app.isLoading = true;
        app.$axios
          .post(this.$endpoint + "checks", app.$data)
          .then((res) => {
            res = res.data;
            app.snakbarText = app.$t("registered");
            app.snakbarColor = "green";
            app.snakbar = true;

            if (app.$refs.refFilePicker !== undefined && app.attachments) {
              app.$refs.refFilePicker.create(res.data.id);
            }
            if (app.id) {
            } else {
              app.$emit("input", res.data.data);
            }
            app.$emit("close", app.$route);
            app.goTo(app.type + "List");
          })
          .catch((err) => {
            if (err.response && err.response.status === 409) {
              this.$store.commit("SET_PLAN_DIALOG", true);
            }
            this.serverErrorHandeler(err);
          })
          .finally(() => {
            this.isLoading = false;
            // this.clearForm()
          });
      }
    },
    show() {
      var app = this;
      app.loading = true;
      app.clearing = true;
      app.showing = true;
      app.$axios
        .get(this.$endpoint + "checks/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          res = res.data;
          app.$nextTick(() => {
            if (res.type === "own") {
              app.type = "payCheck";
              app.check = res.page;
            } else {
              app.type = "recieveCheck";
            }
            app.typeForm = res.type;
            app.id = res.id;
            app.attachments = res.attachments;
            app.fishermanNumber = res.fisherman_number;
            app.account = res.account;
            app.cashBox = res.cashbox;
            app.bank = res.bank;
            app.initialPeriod = res.initial_period;
            app.kind = res.kind;
            app.number = res.number;
            app.date = res.date;
            app.dueDate = res.due_date;
            app.notifications = res.notifications;
            app.branch = res.branch;
            app.amount = res.amount;
            app.bankName = res.bank_name;
            app.bankAccountNumber = res.bank_account_number;
            app.relations = res.relations;
            app.currency = res.currency;
            app.currency_amount = res.currency_amount;
            app.content = res.content;
            app.owner_name = res.owner_name;
            if (res.tags) {
              app.tag = res.tags;
            }
            app.render = true;
          });
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    update() {
      var app = this;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "checks/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          if (app.$refs.refFilePicker !== undefined) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);
          app.goTo(app.type + "List");
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    clearForm() {
      this.initialPeriod = null;
      this.attachments = null;
      this.account = null;
      // this.date = null
      this.check = null;
      this.dueDate = null;
      this.tag = null;
      this.content = null;
      this.valid = null;
      this.message = null;
      this.alertType = null;
      this.bankAccountNumber = null;
      this.amount = null;
      this.relations = null;
      this.name = null;
      this.id = null;
      this.currency_amount = null;
      this.status = "active";
      this.branch = null;
      this.bankName = null;
      this.number = null;
      this.amountDisable = false;
      this.kindDisable = false;
      this.relationsDisable = false;
      this.currencyAmountDisable = false;
      this.dueDateDisable = false;
      this.initial_price = null;
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped></style>
